import { useState, useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { LoadingButton } from "./LoadingButton";

function HeroSection() {
  // Mouse position for cursor light effect
  const mouseMoveRef = useRef<number>(0);
  
  // Refs for container elements
  const heroRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLDivElement>(null);
  
  // Motion values for parallax effects
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Transform mouse position for various effects
  const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
  const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);
  const glowX = useTransform(mouseX, [-100, 100], [-50, 50]);
  const glowY = useTransform(mouseY, [-100, 100], [-50, 50]);
  
  // Handle mouse movement for 3D effect and cursor light
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Calculate mouse position relative to center
        const x = (e.clientX - centerX);
        const y = (e.clientY - centerY);
        
        // Use requestAnimationFrame for performance
        if (mouseMoveRef.current) {
          cancelAnimationFrame(mouseMoveRef.current);
        }
        
        mouseMoveRef.current = requestAnimationFrame(() => {
          mouseX.set(x);
          mouseY.set(y);
        });
      }
    };
    
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (mouseMoveRef.current) {
        cancelAnimationFrame(mouseMoveRef.current);
      }
    };
  }, [mouseX, mouseY]);
  
  // Animated typing effect
  const [displayText, setDisplayText] = useState("");
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const phrases = ["with AI Magic", "in Minutes", "Automatically"];
  const fullText = phrases[currentPhraseIndex];
  
  useEffect(() => {
    let i = 0;
    let direction = 'typing'; // 'typing' or 'deleting'
    
    const typingInterval = setInterval(() => {
      if (direction === 'typing') {
        if (i < fullText.length) {
          setDisplayText(fullText.substring(0, i + 1));
          i++;
        } else {
          // Pause at the end before starting to delete
          setTimeout(() => {
            direction = 'deleting';
          }, 2000);
        }
      } else if (direction === 'deleting') {
        if (i > 0) {
          i--;
          setDisplayText(fullText.substring(0, i));
        } else {
          direction = 'typing';
          // Change to next phrase
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, 100);
    
    return () => clearInterval(typingInterval);
  }, [fullText, currentPhraseIndex]);
  
  return (
    <section className="py-20 px-4 relative" id="hero" ref={heroRef}>
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Text Content */}
          <motion.div 
            className="flex-1 text-center md:text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-2 text-[#694e42] relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Clean Your Inbox
              <motion.div 
                className="absolute -z-10 w-[110%] h-[40%] left-[-5%] bottom-[-5%] bg-[#ff8c42]/10 rounded-full blur-xl"
                animate={{ 
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.05, 1],
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  repeatType: "reverse" 
                }}
              />
            </motion.h1>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-8 relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="bg-gradient-to-r from-[#946e5d] to-[#ff8c42] bg-clip-text text-transparent">
                {displayText}
              </span>
              <motion.span 
                className="inline-block h-12 w-[3px] bg-[#ff8c42] ml-1"
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              
              {/* Text highlight glow */}
              <motion.div
                className="absolute -z-10 w-full h-full left-0 bottom-0 filter blur-lg"
                style={{ 
                  background: `radial-gradient(circle at ${50 + glowX.get() / 5}% ${50 + glowY.get() / 5}%, rgba(255, 140, 66, 0.3), transparent 70%)`,
                  opacity: 0.6
                }}
              />
            </motion.h1>
            
            <motion.p 
              className="text-lg text-[#694e42] mb-8 max-w-xl leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              MailMaid uses AI to organize your messy inbox, eliminate clutter, prioritize important emails, and automate responses. Save hours every week with intelligent email management.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LoadingButton 
                  className="relative px-8 py-3 bg-[#ff8c42] text-white rounded-md font-medium shadow-md hover:bg-[#ff7a29] overflow-hidden"
                  loadingText="Connecting..."
                >
                  {/* Button glow effect */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-[#ff8c42]/0 via-white/30 to-[#ff8c42]/0"
                    animate={{ 
                      x: ['-100%', '200%'],
                    }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity,
                      repeatDelay: 3
                    }}
                    style={{ mixBlendMode: 'overlay' }}
                  />
                  Clean My Inbox
                </LoadingButton>
              </motion.div>
              
              <motion.button
                className="relative px-8 py-3 border border-[#694e42] text-[#694e42] rounded-md font-medium overflow-hidden group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.span 
                  className="absolute inset-0 bg-[#694e42]/5 w-0 group-hover:w-full transition-all duration-300 ease-out"
                  initial={{ width: 0 }}
                />
                See How It Works
              </motion.button>
            </motion.div>
            
            <motion.p 
              className="text-sm text-[#8a7266] mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              No credit card required. Free cleanup for up to <span className="font-semibold text-[#ff8c42]">100 emails</span>.
            </motion.p>
          </motion.div>
          
          {/* Enhanced Dashboard Box with 3D effect and lighting */}
          <motion.div 
            className="flex-1 perspective-[1200px]"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            ref={heroImageRef}
          >
            <motion.div
              className="relative w-full aspect-square max-w-md mx-auto"
              style={{
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
              }}
              transition={{ type: "spring", mass: 5, stiffness: 400, damping: 50 }}
            >
              {/* Dashboard Box with depth effect */}
              <motion.div 
                className="relative bg-white/90 backdrop-blur-md border border-[#e2dad3] rounded-2xl shadow-2xl overflow-hidden aspect-square z-10"
                style={{ 
                  transformStyle: "preserve-3d",
                  transform: "translateZ(0px)",
                  boxShadow: "0 50px 100px -20px rgba(0,0,0,0.15), 0 30px 60px -30px rgba(0,0,0,0.1)"
                }}
              >
                {/* Dashboard Content - Email cleanup mockup */}
                <div className="absolute inset-0 p-6 flex flex-col">
                  {/* Header Bar */}
                  <div className="flex justify-between items-center pb-4 border-b border-[#e2dad3]">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-[#ff8c42] rounded-lg flex items-center justify-center shadow-md">
                        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="ml-3 text-lg font-bold text-[#694e42]">MailMaid</div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-6 h-6 rounded-full bg-[#fff5eb] flex items-center justify-center shadow-sm border border-[#e2dad3]/50">
                        <span className="text-xs font-medium text-[#ff8c42]">4</span>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-[#fff5eb] flex items-center justify-center shadow-sm border border-[#e2dad3]/50">
                        <svg className="w-3 h-3 text-[#694e42]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="currentColor"/>
                          <path d="M18.7273 12C18.7273 12 16.0909 17 12 17C7.90909 17 5.27273 12 5.27273 12C5.27273 12 7.90909 7 12 7C16.0909 7 18.7273 12 18.7273 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Area */}
                  <div className="flex mt-4 h-full">
                    {/* Sidebar */}
                    <div className="w-1/4 pr-3">
                      <motion.div 
                        className="h-10 w-full bg-[#ff8c42] rounded-lg mb-3 flex items-center justify-center text-white text-xs font-semibold"
                        whileHover={{ scale: 1.02 }}
                      >
                        Cleanup
                      </motion.div>
                      <div className="space-y-2">
                        <motion.div 
                          className="h-8 w-full bg-[#f9f6f4] rounded-lg flex items-center px-2 text-xs cursor-pointer"
                          whileHover={{ x: 2, backgroundColor: "#fff5eb" }}
                        >
                          Inbox
                        </motion.div>
                        <motion.div 
                          className="h-8 w-full bg-[#fff5eb] rounded-lg flex items-center px-2 text-xs border border-[#ff8c42]/20 cursor-pointer"
                          whileHover={{ x: 2, backgroundColor: "#fff5eb" }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#ff8c42] mr-1.5"></span>
                          Newsletters
                        </motion.div>
                        <motion.div 
                          className="h-8 w-full bg-[#f9f6f4] rounded-lg flex items-center px-2 text-xs cursor-pointer"
                          whileHover={{ x: 2, backgroundColor: "#fff5eb" }}
                        >
                          Promotions
                        </motion.div>
                        <motion.div 
                          className="h-8 w-full bg-[#f9f6f4] rounded-lg flex items-center px-2 text-xs cursor-pointer"
                          whileHover={{ x: 2, backgroundColor: "#fff5eb" }}
                        >
                          Archive
                        </motion.div>
                      </div>
                    </div>
                    
                    {/* Main Content - Smart Cleanup */}
                    <div className="w-3/4 pl-3 space-y-3">
                      <motion.div 
                        className="bg-[#fff5eb] p-3 rounded-lg border border-[#ff8c42]/30"
                        initial={{ x: 10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1, duration: 0.5 }}
                      >
                        <div className="text-xs font-medium text-[#694e42] mb-2 flex items-center">
                          <svg className="w-3 h-3 mr-1 text-[#ff8c42]" viewBox="0 0 24 24" fill="none">
                            <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" 
                              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          AI Recommendation
                        </div>
                        <div className="text-xs text-[#694e42]">Found 17 newsletters and promotional emails you can safely archive.</div>
                      </motion.div>
                      
                      {/* Email Cards */}
                      {[1, 2, 3].map((item, i) => (
                        <motion.div 
                          key={item} 
                          className={`h-16 w-full ${i === 0 ? 'bg-[#fff5eb] border border-[#ff8c42]/30' : 'bg-[#f9f6f4]'} rounded-lg p-3 flex items-center`}
                          style={{
                            transformStyle: "preserve-3d",
                            transform: `translateZ(${item * 5}px)`,
                            boxShadow: i === 0 ? '0 4px 6px -1px rgba(255, 140, 66, 0.1)' : 'none'
                          }}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 1.2 + i * 0.1, duration: 0.5 }}
                          whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}
                        >
                          <div className="w-5 h-5 rounded-md border border-[#e2dad3] mr-3 flex-shrink-0 flex items-center justify-center overflow-hidden">
                            {i === 0 && (
                              <motion.div 
                                className="w-3 h-3 bg-[#ff8c42]"
                                animate={{ scale: [0, 1] }}
                                transition={{ delay: 1.8, duration: 0.3 }}
                              />
                            )}
                          </div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center">
                                <div className="w-4 h-4 rounded-full bg-[#946e5d] flex-shrink-0"></div>
                                <div className="w-24 h-3 bg-[#e2dad3] ml-2 rounded"></div>
                              </div>
                              <div className="w-16 h-3 bg-[#e2dad3] rounded text-xs font-light"></div>
                            </div>
                            <div className="w-full h-3 bg-[#e2dad3] mt-2 rounded"></div>
                          </div>
                        </motion.div>
                      ))}
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2 mt-3">
                        <motion.div 
                          className="h-8 w-1/3 bg-[#ff8c42] rounded-lg flex items-center justify-center text-white text-xs cursor-pointer"
                          whileHover={{ y: -2, boxShadow: '0 4px 8px rgba(255, 140, 66, 0.2)' }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Archive All
                        </motion.div>
                        <motion.div 
                          className="h-8 w-1/3 bg-[#946e5d] rounded-lg flex items-center justify-center text-white text-xs cursor-pointer"
                          whileHover={{ y: -2, boxShadow: '0 4px 8px rgba(148, 110, 93, 0.2)' }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Unsubscribe
                        </motion.div>
                        <motion.div 
                          className="h-8 w-1/3 bg-[#f9f6f4] rounded-lg flex items-center justify-center text-xs border border-[#e2dad3] cursor-pointer"
                          whileHover={{ y: -2, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)' }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Keep
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Light reflections on glass */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-80 pointer-events-none"
                  style={{ 
                    transform: `translateZ(2px)`,
                  }}
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-80 pointer-events-none"
                  style={{ 
                    transform: `translateZ(2px)`,
                  }}
                  animate={{
                    backgroundPosition: ['0% 0%', '100% 100%'],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Statistics */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            { value: "5+ hrs", label: "Saved per week on average" },
            { value: "90%", label: "Reduction in email clutter" },
            { value: "100K+", label: "Happy users worldwide" }
          ].map((stat, index) => (
            <motion.div 
              key={index}
              className="p-6 bg-white rounded-xl border border-[#e2dad3]/50 backdrop-blur-sm relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              whileHover={{ 
                y: -5, 
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                borderColor: "#e2dad3"
              }}
            >
              {/* Background accent */}
              <motion.div 
                className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-[#ff8c42]/5"
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  delay: index * 0.5
                }}
              />
              
              <motion.div 
                className="text-4xl font-bold text-[#ff8c42] mb-2"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.4 + index * 0.1,
                  type: "spring" 
                }}
              >
                {stat.value}
              </motion.div>
              <p className="text-[#694e42] relative z-10">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HeroSection;